import { useState, useEffect } from "react";
import {
  HiOutlineCloudArrowUp, HiOutlineClock, HiOutlineDocumentText,
  HiOutlineExclamationCircle, HiOutlineShieldCheck,
  HiOutlineArrowDownTray, HiOutlineArrowPath, HiOutlineChatBubbleLeftRight,
} from "react-icons/hi2";
import PortalLayout from "./PortalLayout";
import { portalAPI, portalFileUrl } from "./portalApi";
import "./DocumentsPage.css";

const fmt = (d) => d ? new Date(d).toLocaleDateString("en-US",{ year:"numeric", month:"short", day:"numeric" }) : "—";
const fmtSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024)       return `${bytes} B`;
  if (bytes < 1024*1024)  return `${(bytes/1024).toFixed(1)} KB`;
  return `${(bytes/(1024*1024)).toFixed(1)} MB`;
};

export default function DocumentsPage() {
  const [appDocs,   setAppDocs]   = useState([]);
  const [replyDocs, setReplyDocs] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState("");
  const [tab,       setTab]       = useState("all"); // "all" | "application" | "replies"

  const fetchDocs = async () => {
    setLoading(true); setError("");
    try {
      const res = await portalAPI.documents();
      setAppDocs(res.data?.application_documents || []);
      setReplyDocs(res.data?.reply_attachments   || []);
    } catch (err) { setError("Could not load documents. Please try again."); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchDocs(); }, []);

  const allDocs = [
    ...appDocs.map(d  => ({ ...d,  sourceLabel:"Application Document", sourceColor:"#1e2a1e" })),
    ...replyDocs.map(r => ({ ...r,  sourceLabel:"Message Attachment",   sourceColor:"#b85c38",
      downloadUrl: portalFileUrl(`/portal/reply-attachments/${r.id}/download`) })),
  ].sort((a,b) => new Date(b.created_at) - new Date(a.created_at));

  const displayed = tab === "application" ? allDocs.filter(d => d.source === "application")
                  : tab === "replies"     ? allDocs.filter(d => d.source === "reply")
                  : allDocs;

  const totalCount  = allDocs.length;
  const appCount    = appDocs.length;
  const replyCount  = replyDocs.length;

  return (
    <PortalLayout eyebrow="Documents" pageTitle="My Documents"
      quote="Preparation today strengthens tomorrow." quoteAuthor="Unknown">
      <p className="dp__sub">Your submitted documents and any files you've uploaded in messages all appear here.</p>

      {/* Stats */}
      <div className="dp__stats-row">
        <div className="pc dp__stat">
          <HiOutlineDocumentText className="dp__stat-icon"/>
          <p className="dp__stat-num">{totalCount}</p>
          <p className="dp__stat-label">Total Files</p>
        </div>
        <div className="pc dp__stat">
          <HiOutlineShieldCheck className="dp__stat-icon dp__stat-icon--green"/>
          <p className="dp__stat-num">{appCount}</p>
          <p className="dp__stat-label">Application Docs</p>
        </div>
        <div className="pc dp__stat">
          <HiOutlineChatBubbleLeftRight className="dp__stat-icon dp__stat-icon--rust"/>
          <p className="dp__stat-num">{replyCount}</p>
          <p className="dp__stat-label">Message Attachments</p>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="dp__tabs">
        {[["all","All Documents"],["application","Application Docs"],["replies","Message Attachments"]].map(([v,l]) => (
          <button key={v} type="button"
            className={`dp__tab ${tab===v?"dp__tab--active":""}`}
            onClick={() => setTab(v)}>{l}
          </button>
        ))}
        <button type="button" className="p-btn p-btn--outline dp__refresh-btn" onClick={fetchDocs}>
          <HiOutlineArrowPath /> Refresh
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="dp__loading">
          <div className="dp__spinner"/>
          <p>Loading your documents…</p>
        </div>
      ) : error ? (
        <div className="pc dp__error">
          <HiOutlineExclamationCircle/> {error}
          <button type="button" className="p-btn p-btn--outline" onClick={fetchDocs} style={{marginLeft:"0.75rem"}}>Try again</button>
        </div>
      ) : displayed.length === 0 ? (
        <div className="pc dp__empty">
          <HiOutlineCloudArrowUp className="dp__empty-icon"/>
          <p className="dp__empty-title">No documents here yet</p>
          <p className="dp__empty-sub">
            {tab === "replies"
              ? "Files you attach when replying to team messages will appear here."
              : "Documents submitted with your application will appear here."}
          </p>
        </div>
      ) : (
        <div className="pc dp__table-card">
          <table className="dp__table">
            <thead>
              <tr>
                <th>File</th>
                <th>Type</th>
                <th>Size</th>
                <th>Uploaded</th>
                <th>Download</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map((doc, i) => {
                const dlUrl = doc.downloadUrl ||
                  portalFileUrl(`/portal/documents/${doc.id}/download`);
                return (
                  <tr key={`${doc.source}-${doc.id}-${i}`}>
                    <td>
                      <div className="dp__doc-name-row">
                        <div className="p-icon-circle p-icon-circle--surface"
                          style={{width:"2rem",height:"2rem",fontSize:"0.8rem"}}>
                          <HiOutlineDocumentText />
                        </div>
                        <div>
                          <p className="dp__doc-name">{doc.file_name || doc.name || "File"}</p>
                          {doc.note_context && (
                            <p className="dp__doc-desc" title={doc.note_context}>
                              Reply to: {doc.note_context.slice(0,50)}{doc.note_context.length > 50 ? "…" : ""}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="dp__source-badge"
                        style={{ background:`${doc.sourceColor}15`, color:doc.sourceColor }}>
                        {doc.sourceLabel}
                      </span>
                    </td>
                    <td className="dp__size">{fmtSize(doc.file_size)}</td>
                    <td className="dp__date">{fmt(doc.created_at)}</td>
                    <td>
                      <a href={dlUrl} target="_blank" rel="noreferrer"
                        className="dp__dl-btn" download>
                        <HiOutlineArrowDownTray /> Open
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Info note */}
      <div className="pc dp__info-note">
        <HiOutlineClock style={{flexShrink:0, fontSize:"1.1rem", color:"#6b6258"}}/>
        <p>
          All documents submitted with your application are listed here. Files you attach
          when replying to team messages also appear here. To add more documents,
          reply to a team message with an attachment.
        </p>
      </div>
    </PortalLayout>
  );
}
